/**
 * Clasifica el rechazo de un plugin nativo de login social (Google/Apple, iOS y Android).
 *
 * Separa DOS cosas que antes iban pegadas y por eso se perdían las dos:
 * - `outcome`: qué debe hacer el usuario. Es lo único que decide la UI.
 * - `code`: qué pasó exactamente. Va a telemetría, nunca a la pantalla.
 *
 * El motivo de existir: un usuario que cierra la hoja de Google no ha sufrido un
 * fallo, pero la app le mostraba «No hemos podido iniciar sesión con Google».
 * Eso se reporta como bug con toda la razón. Cancelar es silencioso.
 */

export type NativeAuthFailureOutcome = 'cancelled' | 'no_account' | 'failed'

export interface NativeAuthFailure {
  outcome: NativeAuthFailureOutcome
  code?: string
}

/** El usuario descartó el diálogo del sistema. No es un error: no se le dice nada. */
const CANCELLED_CODES: ReadonlySet<string> = new Set([
  'GOOGLE_SIGN_IN_CANCELLED',
  'APPLE_SIGN_IN_CANCELLED'
])

/**
 * El proveedor nativo no puede autenticar en ESTE dispositivo/build: sin cuenta de
 * Google, sin proveedor de Credential Manager (dispositivos sin Play Services) o sin
 * `serverClientId` en el build. Reintentar no arregla ninguno; hay que ofrecer email.
 * El OAuth web NO es alternativa aquí: la app vive en un WebView y Google lo rechaza
 * con `disallowed_useragent`.
 */
const NO_ACCOUNT_CODES: ReadonlySet<string> = new Set([
  'GOOGLE_SIGN_IN_NO_CREDENTIAL',
  'GOOGLE_SIGN_IN_NO_PROVIDER',
  'GOOGLE_SIGN_IN_UNAVAILABLE',
  'APPLE_SIGN_IN_NO_CREDENTIAL'
])

function readFailureCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined

  const code = (error as { code?: unknown }).code

  return typeof code === 'string' && code.length > 0 ? code : undefined
}

function classifyCode(code: string): NativeAuthFailureOutcome {
  if (CANCELLED_CODES.has(code)) return 'cancelled'
  if (NO_ACCOUNT_CODES.has(code)) return 'no_account'

  return 'failed'
}

export function classifyNativeAuthFailure(error: unknown): NativeAuthFailure {
  const code = readFailureCode(error)
  if (!code) return { outcome: 'failed' }

  return { outcome: classifyCode(code), code }
}
